import { useEffect, useMemo, useState } from 'react';
import { Layout } from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from '@/hooks/use-toast';
import { footerApi, type FooterGroup, type FooterItem } from '../api';
import { env } from '@/config/env';
import { Plus, Loader2, Pencil, Trash2, ArrowUp, ArrowDown } from 'lucide-react';

function toAbsoluteUrl(u?: string) {
  if (!u) return '';
  if (/^https?:\/\//i.test(u)) return u;
  const base = env.apiUrl.replace(/\/$/, '');
  return `${base}/${u.replace(/^\//, '')}`;
}

type GroupFormState = {
  label: string;
  slug: string;
  is_active: boolean;
};

type ItemFormState = {
  label: string;
  url: string;
  is_active: boolean;
  image: File | null;
};

const emptyGroupForm = (): GroupFormState => ({ label: '', slug: '', is_active: true });
const emptyItemForm = (): ItemFormState => ({ label: '', url: '', is_active: true, image: null });

export default function FooterAdminPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [groups, setGroups] = useState<FooterGroup[]>([]);

  const [groupOpen, setGroupOpen] = useState(false);
  const [groupEditing, setGroupEditing] = useState<FooterGroup | null>(null);
  const [groupForm, setGroupForm] = useState<GroupFormState>(emptyGroupForm);
  const [groupDeleting, setGroupDeleting] = useState<FooterGroup | null>(null);

  const [itemOpen, setItemOpen] = useState(false);
  const [itemGroupId, setItemGroupId] = useState<string>('');
  const [itemEditing, setItemEditing] = useState<FooterItem | null>(null);
  const [itemForm, setItemForm] = useState<ItemFormState>(emptyItemForm);
  const [itemDeleting, setItemDeleting] = useState<FooterItem | null>(null);

  const sortedGroups = useMemo(
    () => [...groups].sort((a, b) => Number(a.sort_order || 0) - Number(b.sort_order || 0)),
    [groups]
  );

  const load = async () => {
    setLoading(true);
    try {
      const res = await footerApi.listGroups();
      setGroups(res || []);
    } catch (error: any) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error?.response?.data?.message || 'No se pudo cargar el footer.',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openCreateGroup = () => {
    setGroupEditing(null);
    setGroupForm(emptyGroupForm());
    setGroupOpen(true);
  };

  const openEditGroup = (group: FooterGroup) => {
    setGroupEditing(group);
    setGroupForm({
      label: group.label || '',
      slug: group.slug || '',
      is_active: group.is_active !== false,
    });
    setGroupOpen(true);
  };

  const saveGroup = async () => {
    if (!groupForm.label.trim()) {
      toast({ variant: 'destructive', title: 'Validación', description: 'label es obligatorio.' });
      return;
    }
    setSaving(true);
    try {
      if (groupEditing) {
        await footerApi.updateGroup(groupEditing.id, {
          label: groupForm.label,
          slug: groupForm.slug,
          is_active: groupForm.is_active,
        });
        toast({ title: 'Grupo actualizado' });
      } else {
        await footerApi.createGroup({
          label: groupForm.label,
          slug: groupForm.slug,
          is_active: groupForm.is_active,
        });
        toast({ title: 'Grupo creado' });
      }
      setGroupOpen(false);
      await load();
    } catch (error: any) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error?.response?.data?.message || 'No se pudo guardar el grupo.',
      });
    } finally {
      setSaving(false);
    }
  };

  const removeGroup = async (group: FooterGroup) => {
    try {
      await footerApi.deleteGroup(group.id);
      toast({ title: 'Grupo eliminado' });
      await load();
    } catch {
      toast({ variant: 'destructive', title: 'Error', description: 'No se pudo eliminar el grupo.' });
    }
  };

  const moveGroup = async (index: number, dir: -1 | 1) => {
    const target = index + dir;
    if (target < 0 || target >= sortedGroups.length) return;
    const next = [...sortedGroups];
    const tmp = next[index];
    next[index] = next[target];
    next[target] = tmp;
    setGroups(next);
    try {
      await footerApi.reorderGroups(next.map((g) => g.id));
      await load();
    } catch {
      toast({ variant: 'destructive', title: 'Error', description: 'No se pudo guardar orden de grupos.' });
      await load();
    }
  };

  const openCreateItem = (groupId: string) => {
    setItemGroupId(groupId);
    setItemEditing(null);
    setItemForm(emptyItemForm());
    setItemOpen(true);
  };

  const openEditItem = (groupId: string, item: FooterItem) => {
    setItemGroupId(groupId);
    setItemEditing(item);
    setItemForm({
      label: item.label || '',
      url: item.url || '',
      is_active: item.is_active !== false,
      image: null,
    });
    setItemOpen(true);
  };

  const saveItem = async () => {
    if (!itemEditing && !itemForm.image) {
      toast({ variant: 'destructive', title: 'Validación', description: 'Debes subir una imagen.' });
      return;
    }
    if (itemForm.url && !/^https?:\/\//i.test(itemForm.url)) {
      toast({ variant: 'destructive', title: 'Validación', description: 'url debe iniciar con http:// o https://' });
      return;
    }

    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('label', itemForm.label);
      fd.append('url', itemForm.url);
      fd.append('is_active', String(itemForm.is_active));
      if (itemForm.image) fd.append('image', itemForm.image);

      if (itemEditing) {
        await footerApi.updateItem(itemEditing.id, fd);
        toast({ title: 'Item actualizado' });
      } else {
        await footerApi.createItem(itemGroupId, fd);
        toast({ title: 'Item creado' });
      }
      setItemOpen(false);
      await load();
    } catch (error: any) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error?.response?.data?.message || 'No se pudo guardar el item.',
      });
    } finally {
      setSaving(false);
    }
  };

  const removeItem = async (item: FooterItem) => {
    try {
      await footerApi.deleteItem(item.id);
      toast({ title: 'Item eliminado' });
      await load();
    } catch {
      toast({ variant: 'destructive', title: 'Error', description: 'No se pudo eliminar el item.' });
    }
  };

  const moveItem = async (group: FooterGroup, index: number, dir: -1 | 1) => {
    const items = [...(group.items || [])].sort((a, b) => Number(a.sort_order || 0) - Number(b.sort_order || 0));
    const target = index + dir;
    if (target < 0 || target >= items.length) return;
    const next = [...items];
    const tmp = next[index];
    next[index] = next[target];
    next[target] = tmp;

    try {
      await footerApi.reorderItems(group.id, next.map((i) => i.id));
      await load();
    } catch {
      toast({ variant: 'destructive', title: 'Error', description: 'No se pudo guardar orden de items.' });
      await load();
    }
  };

  return (
    <Layout>
      <div className="container mx-auto px-4 py-8 space-y-6">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h1 className="text-3xl font-bold">Footer del Listado</h1>
            <p className="text-muted-foreground">Gestiona grupos y logos del footer público.</p>
          </div>
          <Button onClick={openCreateGroup}>
            <Plus className="h-4 w-4 mr-2" />
            Nuevo grupo
          </Button>
        </div>

        {loading ? (
          <Card className="p-6 flex items-center gap-2 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Cargando footer...
          </Card>
        ) : sortedGroups.length === 0 ? (
          <Card className="p-8 text-center text-muted-foreground">No hay grupos configurados.</Card>
        ) : (
          <div className="space-y-5">
            {sortedGroups.map((group, gIndex) => {
              const items = [...(group.items || [])].sort((a, b) => Number(a.sort_order || 0) - Number(b.sort_order || 0));
              return (
                <Card key={group.id} className="p-4 space-y-4">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <h2 className="text-lg font-semibold">{group.label}</h2>
                      <p className="text-sm text-muted-foreground">slug: {group.slug}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={group.is_active !== false}
                        onCheckedChange={async (value) => {
                          try {
                            await footerApi.updateGroup(group.id, { is_active: value });
                            await load();
                          } catch {
                            toast({ variant: 'destructive', title: 'Error', description: 'No se pudo actualizar grupo.' });
                          }
                        }}
                      />
                      <Button variant="outline" size="sm" onClick={() => moveGroup(gIndex, -1)} disabled={gIndex === 0}><ArrowUp className="h-4 w-4" /></Button>
                      <Button variant="outline" size="sm" onClick={() => moveGroup(gIndex, 1)} disabled={gIndex === sortedGroups.length - 1}><ArrowDown className="h-4 w-4" /></Button>
                      <Button variant="outline" size="sm" onClick={() => openEditGroup(group)}><Pencil className="h-4 w-4" /></Button>
                      <Button variant="destructive" size="sm" onClick={() => setGroupDeleting(group)}><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  </div>

                  <div className="border-t pt-4">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-medium">Items</h3>
                      <Button size="sm" onClick={() => openCreateItem(group.id)}>
                        <Plus className="h-4 w-4 mr-2" /> Nuevo item
                      </Button>
                    </div>

                    {items.length === 0 ? (
                      <p className="text-sm text-muted-foreground">Sin items.</p>
                    ) : (
                      <div className="space-y-3">
                        {items.map((item, index) => (
                          <div key={item.id} className="flex flex-wrap items-center gap-3 rounded border p-3">
                            <img
                              src={toAbsoluteUrl(item.image_path)}
                              alt={item.label || item.id}
                              className="h-16 w-16 object-contain rounded border bg-white"
                            />
                            <div className="min-w-[220px] flex-1">
                              <p className="font-medium">{item.label || 'Sin etiqueta'}</p>
                              <p className="text-sm text-muted-foreground truncate">{item.url || 'Sin URL'}</p>
                            </div>
                            <div className="flex items-center gap-2">
                              <Switch
                                checked={item.is_active !== false}
                                onCheckedChange={async (value) => {
                                  try {
                                    await footerApi.updateItem(item.id, { is_active: value });
                                    await load();
                                  } catch {
                                    toast({ variant: 'destructive', title: 'Error', description: 'No se pudo actualizar item.' });
                                  }
                                }}
                              />
                              <Button variant="outline" size="sm" onClick={() => moveItem(group, index, -1)} disabled={index === 0}><ArrowUp className="h-4 w-4" /></Button>
                              <Button variant="outline" size="sm" onClick={() => moveItem(group, index, 1)} disabled={index === items.length - 1}><ArrowDown className="h-4 w-4" /></Button>
                              <Button variant="outline" size="sm" onClick={() => openEditItem(group.id, item)}><Pencil className="h-4 w-4" /></Button>
                              <Button variant="destructive" size="sm" onClick={() => setItemDeleting(item)}><Trash2 className="h-4 w-4" /></Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      <Dialog open={groupOpen} onOpenChange={setGroupOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{groupEditing ? 'Editar grupo' : 'Nuevo grupo'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Label *</Label>
              <Input value={groupForm.label} onChange={(e) => setGroupForm((s) => ({ ...s, label: e.target.value }))} />
            </div>
            <div className="space-y-2">
              <Label>Slug (opcional)</Label>
              <Input value={groupForm.slug} onChange={(e) => setGroupForm((s) => ({ ...s, slug: e.target.value }))} />
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={groupForm.is_active} onCheckedChange={(v) => setGroupForm((s) => ({ ...s, is_active: v }))} />
              <span>{groupForm.is_active ? 'Activo' : 'Inactivo'}</span>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setGroupOpen(false)}>Cancelar</Button>
              <Button onClick={saveGroup} disabled={saving}>
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Guardar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={itemOpen} onOpenChange={setItemOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{itemEditing ? 'Editar item' : 'Nuevo item'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Etiqueta (opcional)</Label>
              <Input value={itemForm.label} onChange={(e) => setItemForm((s) => ({ ...s, label: e.target.value }))} />
            </div>
            <div className="space-y-2">
              <Label>URL (opcional)</Label>
              <Input value={itemForm.url} placeholder="https://..." onChange={(e) => setItemForm((s) => ({ ...s, url: e.target.value }))} />
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={itemForm.is_active} onCheckedChange={(v) => setItemForm((s) => ({ ...s, is_active: v }))} />
              <span>{itemForm.is_active ? 'Activo' : 'Inactivo'}</span>
            </div>
            <div className="space-y-2">
              <Label>Imagen {itemEditing ? '(reemplazo opcional)' : '(obligatoria)'}</Label>
              <Input type="file" accept="image/*" onChange={(e) => setItemForm((s) => ({ ...s, image: e.target.files?.[0] || null }))} />
              <p className="text-xs text-muted-foreground">Dimensión recomendada: 256 x 256 (contain)</p>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setItemOpen(false)}>Cancelar</Button>
              <Button onClick={saveItem} disabled={saving}>
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Guardar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!groupDeleting} onOpenChange={(v) => { if (!v) setGroupDeleting(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar grupo</AlertDialogTitle>
            <AlertDialogDescription>
              {groupDeleting ? `¿Seguro que deseas eliminar el grupo ${groupDeleting.label}?` : '¿Eliminar grupo?'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setGroupDeleting(null)}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                if (!groupDeleting) return;
                const target = groupDeleting;
                setGroupDeleting(null);
                await removeGroup(target);
              }}
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!itemDeleting} onOpenChange={(v) => { if (!v) setItemDeleting(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar item</AlertDialogTitle>
            <AlertDialogDescription>
              {itemDeleting ? `¿Seguro que deseas eliminar el item ${itemDeleting.label || itemDeleting.id}?` : '¿Eliminar item?'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setItemDeleting(null)}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                if (!itemDeleting) return;
                const target = itemDeleting;
                setItemDeleting(null);
                await removeItem(target);
              }}
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}
